import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { theme } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { DEFAULT_SOV_WEIGHTS } from '../../utils/sovScoreCalculations';
import type { SovWeights, SovScoreResult } from '../../types/workplace';

interface SovWeightStepProps {
  weights: SovWeights;
  onChangeWeight: (sovId: string, value: number) => void;
  onResetWeights: () => void;
  istScore: SovScoreResult;
  onContinue: () => void;
  onBack: () => void;
}

const SOV_ITEMS: { id: string; labelKey: string; color: string }[] = [
  { id: 'sov-1', labelKey: 'workplace.sov.sov1', color: '#F7675E' },
  { id: 'sov-2', labelKey: 'workplace.sov.sov2', color: '#62C7EF' },
  { id: 'sov-3', labelKey: 'workplace.sov.sov3', color: '#2C8F92' },
  { id: 'sov-4', labelKey: 'workplace.sov.sov4', color: '#3D61AA' },
  { id: 'sov-5', labelKey: 'workplace.sov.sov5', color: '#E8A04C' },
  { id: 'sov-6', labelKey: 'workplace.sov.sov6', color: '#6BB89C' },
  { id: 'sov-7', labelKey: 'workplace.sov.sov7', color: '#9B6BB8' },
  { id: 'sov-8', labelKey: 'workplace.sov.sov8', color: '#A1A7AE' },
];

/** EU Cloud Sovereignty Framework definitions per objective */
const SOV_DEFINITIONS: Record<string, { de: string; en: string }> = {
  'sov-1': {
    de: 'Strategische Souveränität bewertet, inwieweit die Dienste eines Cloud-Anbieters im europäischen Rechts-, Finanz- und Industrieökosystem verankert sind — Eigentümerstruktur, Governance und Ausrichtung an EU-Prioritäten.',
    en: 'Strategic sovereignty captures the degree to which cloud services are anchored within the EU legal, financial, and industrial ecosystem — ownership stability, governance influence, and alignment with EU strategic priorities.',
  },
  'sov-2': {
    de: 'Rechtliche & jurisdiktionelle Souveränität bewertet die rechtliche Umgebung, die Exposition gegenüber ausländischer Autorität und die Durchsetzbarkeit von Rechten — inwieweit Dienste in europäischer Jurisdiktion verankert und vor externen Rechtsansprüchen geschützt sind.',
    en: 'Legal & jurisdictional sovereignty evaluates the legal environment, exposure to foreign authority, and enforceability of rights — the extent to which services are anchored in European jurisdiction and insulated from external legal claims.',
  },
  'sov-3': {
    de: 'Daten- & KI-Souveränität fokussiert auf Schutz, Kontrolle und Unabhängigkeit von Daten und KI-Diensten innerhalb der EU — wo Daten verarbeitet werden und welchen Grad an Autonomie Kunden über KI-Fähigkeiten behalten.',
    en: 'Data & AI sovereignty focuses on the protection, control, and independence of data assets and AI services within the EU — where data is processed and the degree of autonomy customers retain over AI capabilities.',
  },
  'sov-4': {
    de: 'Operationelle Souveränität misst die praktische Fähigkeit von EU-Akteuren, Technologie unabhängig von ausländischer Kontrolle zu betreiben, zu unterstützen und weiterzuentwickeln — Betriebskontinuität, Verfügbarkeit von Fachkräften und Resilienz.',
    en: 'Operational sovereignty measures the practical ability of EU actors to run, support, and evolve technology independently of foreign control — continuity of operations, skill availability, and resilience against external dependencies.',
  },
  'sov-5': {
    de: 'Lieferketten-Souveränität bewertet die geografische Herkunft, Transparenz und Resilienz der Technologie-Lieferkette — inwieweit kritische Komponenten und Prozesse unter EU-Kontrolle stehen oder Non-EU-Abhängigkeiten ausgesetzt sind.',
    en: 'Supply chain sovereignty evaluates the geographic origin, transparency, and resilience of the technology supply chain — the extent to which critical components and processes remain under EU control or are exposed to non-EU dependencies.',
  },
  'sov-6': {
    de: 'Technologische Souveränität bewertet den Grad an Offenheit, Transparenz und Unabhängigkeit des technologischen Stacks — ob EU-Akteure interoperieren, auditieren und Lösungen ohne Lock-in zu proprietären Systemen weiterentwickeln können.',
    en: 'Technology sovereignty evaluates the degree of openness, transparency, and independence in the underlying technological stack — ensuring EU actors can interoperate, audit, and evolve solutions without lock-in to foreign proprietary systems.',
  },
  'sov-7': {
    de: 'Sicherheits- & Compliance-Souveränität misst, inwieweit Sicherheitsoperationen, Compliance-Verpflichtungen und Resilienzmaßnahmen innerhalb der EU kontrolliert werden — Unabhängigkeit von ausländischen Jurisdiktionen und langfristige operative Sicherheit.',
    en: 'Security & compliance sovereignty measures the extent to which security operations, compliance obligations, and resilience measures are controlled within the EU — ensuring independence from foreign jurisdictions and long-term operational assurance.',
  },
  'sov-8': {
    de: 'Ökologische Nachhaltigkeit bewertet Autonomie und Resilienz von Cloud-Diensten in Bezug auf Energienutzung, Abhängigkeit und Rohstoffknappheit.',
    en: 'Environmental sustainability assesses autonomy and resilience of cloud services over the long term in relation to energy usage, dependency, and raw material scarcity.',
  },
};

const STEP = 5;

// Convert default weights to importance values (0-100 scale)
function weightsToImportance(w: SovWeights): Record<string, number> {
  // Scale so that the max default weight maps to ~75
  const result: Record<string, number> = {};
  SOV_ITEMS.forEach(({ id }) => {
    result[id] = Math.round((w[id] ?? 0) * 100 / 0.20 * 75 / 100);
  });
  return result;
}

function importanceToWeights(imp: Record<string, number>): SovWeights {
  const total = SOV_ITEMS.reduce((s, { id }) => s + (imp[id] ?? 1), 0);
  const w: SovWeights = {};
  if (total === 0) {
    // All zero — equal distribution
    SOV_ITEMS.forEach(({ id }) => { w[id] = 1 / SOV_ITEMS.length; });
  } else {
    SOV_ITEMS.forEach(({ id }) => {
      w[id] = Math.round(((imp[id] ?? 0) / total) * 1000) / 1000;
    });
    // Fix rounding to ensure sum = 1.0
    const sum = Object.values(w).reduce((s, v) => s + v, 0);
    const diff = 1.0 - sum;
    if (Math.abs(diff) > 0.0001) {
      w[SOV_ITEMS[0].id] = Math.round((w[SOV_ITEMS[0].id] + diff) * 1000) / 1000;
    }
  }
  return w;
}

export const SovWeightStep: React.FC<SovWeightStepProps> = ({
  weights,
  onChangeWeight,
  onResetWeights,
  onContinue,
  onBack,
}) => {
  const { t, language } = useLanguage();

  // Track which info tooltip is expanded
  const [expandedInfo, setExpandedInfo] = useState<string | null>(null);

  // Local importance state (0-100 per objective)
  const [importance, setImportance] = useState<Record<string, number>>(() =>
    weightsToImportance(weights)
  );

  // Sync weights to parent whenever importance changes
  const normalizedWeights = useMemo(() => importanceToWeights(importance), [importance]);

  useEffect(() => {
    SOV_ITEMS.forEach(({ id }) => {
      if (Math.abs((normalizedWeights[id] ?? 0) - (weights[id] ?? 0)) > 0.002) {
        onChangeWeight(id, normalizedWeights[id]);
      }
    });
  }, [normalizedWeights, weights, onChangeWeight]);

  const handleAdjust = useCallback((id: string, delta: number) => {
    setImportance(prev => ({
      ...prev,
      [id]: Math.max(0, Math.min(100, (prev[id] ?? 0) + delta)),
    }));
  }, []);

  const handleReset = useCallback(() => {
    setImportance(weightsToImportance(DEFAULT_SOV_WEIGHTS));
    onResetWeights();
  }, [onResetWeights]);

  // Pie chart data
  const pieData = useMemo(() =>
    SOV_ITEMS.map(({ id, labelKey, color }) => ({
      name: t(labelKey as any),
      value: Math.round((normalizedWeights[id] ?? 0) * 100),
      color,
    })),
  [normalizedWeights, t]);

  const btnStyle: React.CSSProperties = {
    width: 36, height: 36,
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    background: '#fff',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
    touchAction: 'manipulation',
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '8px 12px' }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: theme.text }}>
        {t('workplace.sovWeights.title')}
      </h2>
      <p style={{ fontSize: 13, color: theme.muted, margin: '6px 0 16px' }}>
        {t('workplace.sovWeights.description')}
      </p>

      {/* Importance bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {SOV_ITEMS.map(({ id, labelKey, color }) => {
          const val = importance[id] ?? 0;
          const relPct = Math.round((normalizedWeights[id] ?? 0) * 100);
          return (
            <div key={id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.text, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {t(labelKey as any)}
                  <button
                    onClick={() => setExpandedInfo(expandedInfo === id ? null : id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      fontSize: 14, color: expandedInfo === id ? theme.secondary : theme.muted,
                      lineHeight: 1, display: 'inline-flex', alignItems: 'center',
                    }}
                    aria-label={`Info: ${t(labelKey as any)}`}
                  >ⓘ</button>
                </span>
                <span style={{ fontSize: 11, color: theme.muted }}>{relPct}%</span>
              </div>
              {expandedInfo === id && (
                <div style={{
                  fontSize: 12, lineHeight: 1.5, color: theme.muted,
                  background: theme.cardBg, borderRadius: 4, padding: '8px 10px',
                  marginBottom: 6, borderLeft: `3px solid ${color}`,
                }}>
                  {SOV_DEFINITIONS[id]?.[language as 'de' | 'en'] ?? SOV_DEFINITIONS[id]?.de}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => handleAdjust(id, -STEP)} disabled={val <= 0} style={{ ...btnStyle, opacity: val <= 0 ? 0.3 : 1 }}>−</button>
                <div style={{ flex: 1, height: 24, background: theme.cardBg, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${val}%`, background: color, borderRadius: 4, transition: 'width 0.15s ease', minWidth: val > 0 ? 3 : 0 }} />
                </div>
                <button onClick={() => handleAdjust(id, STEP)} disabled={val >= 100} style={{ ...btnStyle, opacity: val >= 100 ? 0.3 : 1 }}>+</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pie Chart — relative distribution */}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: theme.text, margin: '0 0 8px' }}>Relative Gewichtung</p>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              innerRadius={40}
              paddingAngle={2}
              label={false}
              labelLine={false}
            >
              {pieData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', justifyContent: 'center', marginTop: 4 }}>
          {pieData.map((entry, i) => (
            <span key={i} style={{ fontSize: 11, color: theme.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, display: 'inline-block' }} />
              {entry.name}
            </span>
          ))}
        </div>
      </div>

      {/* Reset */}
      <button
        onClick={handleReset}
        style={{
          marginTop: 14, padding: '6px 14px', border: `1px solid ${theme.border}`,
          background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: theme.text,
        }}
      >
        {t('workplace.sovWeights.resetDefaults')}
      </button>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px 20px', border: `1px solid ${theme.border}`, background: '#fff',
            color: theme.text, fontWeight: 700, fontSize: 14, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.sovWeights.back')}
        </button>
        <button
          onClick={onContinue}
          style={{
            padding: '10px 20px', border: `1px solid ${theme.border}`, background: '#fff',
            color: theme.muted, fontWeight: 600, fontSize: 13, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.sovWeights.skip')}
        </button>
        <button
          onClick={onContinue}
          style={{
            padding: '10px 20px', border: `1px solid ${theme.primaryBorder}`,
            background: theme.primarySoft,
            color: theme.text,
            fontWeight: 700, fontSize: 14, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.sovWeights.continue')}
        </button>
      </div>
    </div>
  );
};
